import DemoBanner from "./DemoBanner";
import { Coffee, Clock, MapPin, Star } from "lucide-react";

const menuItems = [
  { name: "House Blend", price: "$4.50", desc: "Rich, smooth, our signature" },
  { name: "Lavender Oat Latte", price: "$6.00", desc: "Floral, creamy, calming" },
  { name: "Cold Brew Float", price: "$5.50", desc: "Vanilla bean ice cream on tap" },
  { name: "Matcha Honey", price: "$5.75", desc: "Earthy meets sweet" },
];

export default function DailyGrind() {
  return (
    <div data-testid="demo-daily-grind" style={{ fontFamily: "'DM Sans', sans-serif", background: "#F5F0E8" }}>
      {/* Nav */}
      <nav className="px-6 md:px-16 py-5 flex items-center justify-between">
        <span style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl md:text-3xl font-bold text-[#3D2B1F] italic">
          The Daily Grind
        </span>
        <div className="hidden md:flex items-center gap-8 text-sm font-medium text-[#3D2B1F]/70">
          <a href="#menu" className="hover:text-[#3D2B1F] transition-colors">Menu</a>
          <a href="#story" className="hover:text-[#3D2B1F] transition-colors">Our Story</a>
          <a href="#visit" className="hover:text-[#3D2B1F] transition-colors">Visit</a>
          <button className="bg-[#3D2B1F] text-[#F5F0E8] px-5 py-2.5 rounded-full text-sm font-semibold hover:bg-[#C67C5B] transition-colors">
            Order Online
          </button>
        </div>
      </nav>

      {/* Hero */}
      <section className="px-6 md:px-16 pt-12 pb-24 md:pt-20 md:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-7xl mx-auto">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#8B9E7E]/20 text-[#5A7252] text-xs font-semibold uppercase tracking-widest px-4 py-2 rounded-full mb-6">
              <Coffee size={14} />
              Est. 2019 &middot; Locally Roasted
            </div>
            <h1 style={{ fontFamily: "'Playfair Display', serif" }} className="text-5xl sm:text-6xl lg:text-7xl font-bold text-[#3D2B1F] leading-[1.05]">
              Coffee that
              <br />
              <span className="italic text-[#C67C5B]">feels like</span>
              <br />
              home.
            </h1>
            <p className="text-lg text-[#3D2B1F]/60 mt-6 max-w-md leading-relaxed">
              A neighborhood spot for slow mornings, great conversation, and the best cup you'll have today.
            </p>
            <div className="flex flex-wrap gap-4 mt-8">
              <button className="bg-[#3D2B1F] text-[#F5F0E8] px-8 py-4 rounded-full font-semibold hover:bg-[#C67C5B] transition-colors">
                See Our Menu
              </button>
              <button className="border-2 border-[#3D2B1F]/20 text-[#3D2B1F] px-8 py-4 rounded-full font-semibold hover:border-[#3D2B1F] transition-colors">
                Find Us
              </button>
            </div>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl rotate-2 hover:rotate-0 transition-transform duration-500">
              <img
                src="https://images.unsplash.com/photo-1759661711730-cd1bb54a6dc1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxjb3p5JTIwbG9jYWwlMjBjb2ZmZWUlMjBzaG9wJTIwaW50ZXJpb3IlMjB3YXJtfGVufDB8fHx8MTc3NjIwMDkxMnww&ixlib=rb-4.1.0&q=85"
                alt="Cafe interior"
                className="w-full h-[400px] md:h-[500px] object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-[#8B9E7E] text-white p-5 rounded-2xl shadow-lg">
              <div className="flex items-center gap-1">
                {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="white" strokeWidth={0} />)}
              </div>
              <p className="text-sm font-semibold mt-1">"Best coffee in town"</p>
            </div>
          </div>
        </div>
      </section>

      {/* Menu Section */}
      <section id="menu" className="bg-[#3D2B1F] py-24 md:py-32 px-6 md:px-16">
        <div className="max-w-5xl mx-auto">
          <p className="text-[#C67C5B] text-sm font-semibold uppercase tracking-widest mb-3">What We Brew</p>
          <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-4xl sm:text-5xl font-bold text-[#F5F0E8] mb-16">
            Fan Favorites
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {menuItems.map((item) => (
              <div key={item.name} className="bg-[#F5F0E8]/8 border border-[#F5F0E8]/15 rounded-2xl p-6 hover:bg-[#F5F0E8]/12 transition-colors">
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="text-xl font-bold text-[#F5F0E8]">{item.name}</h3>
                    <p className="text-[#F5F0E8]/50 text-sm mt-1">{item.desc}</p>
                  </div>
                  <span style={{ fontFamily: "'Playfair Display', serif" }} className="text-2xl font-bold text-[#C67C5B]">
                    {item.price}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Story */}
      <section id="story" className="py-24 md:py-32 px-6 md:px-16 bg-[#F5F0E8]">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="rounded-3xl overflow-hidden -rotate-1 hover:rotate-0 transition-transform duration-500">
            <img
              src="https://images.unsplash.com/photo-1761102913143-520de83bfb45?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2ODh8MHwxfHNlYXJjaHwyfHxsYXR0ZSUyMGFydCUyMGNvZmZlZSUyMGN1cCUyMG92ZXJoZWFkfGVufDB8fHx8MTc3NjIxMjQ5MXww&ixlib=rb-4.1.0&q=85"
              alt="Coffee and pastry"
              className="w-full h-[400px] object-cover"
            />
          </div>
          <div>
            <p className="text-[#8B9E7E] text-sm font-semibold uppercase tracking-widest mb-3">Our Story</p>
            <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-4xl font-bold text-[#3D2B1F] mb-6">
              Started with a dream <span className="italic text-[#C67C5B]">&amp; a French press</span>
            </h2>
            <p className="text-[#3D2B1F]/60 leading-relaxed mb-4">
              What began as a weekend pop-up at the farmer's market turned into the neighborhood's favorite morning ritual. We source beans directly from small farms, roast them in-house, and serve every cup with care.
            </p>
            <p className="text-[#3D2B1F]/60 leading-relaxed">
              The Daily Grind isn't just a coffee shop — it's where neighbors become friends, where freelancers find their flow, and where every sip reminds you that the little things matter most.
            </p>
          </div>
        </div>
      </section>

      {/* Visit */}
      <section id="visit" className="bg-[#8B9E7E] py-24 md:py-32 px-6 md:px-16">
        <div className="max-w-4xl mx-auto text-center">
          <h2 style={{ fontFamily: "'Playfair Display', serif" }} className="text-4xl sm:text-5xl font-bold text-white mb-8">
            Come say hello.
          </h2>
          <div className="flex flex-wrap justify-center gap-8 text-white/80">
            <div className="flex items-center gap-2">
              <MapPin size={18} />
              <span>142 Maple St, Anytown</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={18} />
              <span>Mon-Fri 7am-5pm &middot; Sat-Sun 8am-4pm</span>
            </div>
          </div>
          <button className="mt-10 bg-[#3D2B1F] text-[#F5F0E8] px-10 py-4 rounded-full font-semibold text-lg hover:bg-[#F5F0E8] hover:text-[#3D2B1F] transition-colors">
            Get Directions
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#3D2B1F] py-8 px-6 md:px-16 text-center">
        <span style={{ fontFamily: "'Playfair Display', serif" }} className="text-[#F5F0E8]/40 text-sm italic">
          {new Date().getFullYear()} The Daily Grind. All rights reserved.
        </span>
      </footer>

      <div className="pb-16" />
      <DemoBanner />
    </div>
  );
}
